import { useEffect, useState } from 'react';
import { Box, Grid, Button, Typography } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useLocation } from 'react-router-dom';
import SubscriptionCard from './components/SubscriptionCard';
import config from '../../config';
import { paymentApi } from '../../api/services/userApi';
import { useAppSelector } from '../../store/hooks';
import PageTitle from '../../components/PageTitle';
import PromoCodeModal from './components/PromoCodeModal';
import track from '../../utils/googleAnalyticsEvents';
import PlanSlider from '../../components/PlanSlider';

type PlanType = {
  id?: string | null;
  name: string;
  price: string;
  description?: string;
  features: string[];
};

function SubscriptionPage() {
  const user = useAppSelector((state) => state.userSlice.user);
  const userSubscription = user.subscription;
  const subscriptionPlans = Object.values(config.subscriptionPlans);
  const promoCodePlans = config.promoCodePlans as Record<string, PlanType>;
  const [openModal, setOpenModal] = useState(false);
  // Resolved server-side at signup (see resolveFirstPromoterCampaign on the backend) - no longer
  // guessed at client-side from a hardcoded campaign-name -> coupon map. Any Stripe coupon for
  // the campaign auto-applies at checkout creation time (createSubscriptionCheckout.ts) and shows
  // up on Stripe's own hosted checkout page - no need to preview it here.
  const isFreeViaCampaign = userSubscription?.type?.toLowerCase() === config.adminFreeSubscription;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get('session_id');
  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);
  const onSubscription = async (priceId: string) => {
    try {
      const res = await paymentApi.checkoutSubscription(
        priceId,
      );
      if (res.status === StatusCodes.OK) {
        const link = document.createElement('a');
        link.href = res.data.data?.session.url || '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (sessionId) {
      track.subscriptionEvent();
    }
  }, [sessionId]);

  return (
    <Box
      sx={{
        py: 3,
        px: 4,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <PageTitle
        title="Subscriptions"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          display={userSubscription && promoCodePlans[userSubscription.type] ? 'none' : 'block'}
        >
          <Button
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'capitalize',
            }}
            variant="contained"
            onClick={handleOpen}
          >
            Enter Promo Code
          </Button>
          <PromoCodeModal open={openModal} handleClose={handleClose} onSubmit={onSubscription} />
        </Box>
      </PageTitle>
      <Grid container spacing={3}>
        {isFreeViaCampaign && (
          <Grid item xs={12} display="flex" justifyContent="center">
            <Typography
              variant="h6"
              sx={{
                width: { xs: '100%', lg: '80%' },
                textAlign: 'center',
                bgcolor: 'white',
                px: 5,
                py: 2,
                borderRadius: 2,
                fontWeight: 'bold',
              }}
            >
              You&apos;re on a free plan
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} display="flex" justifyContent="center">
          <PlanSlider />
        </Grid>
        {subscriptionPlans.map((item, index) => (
          <Grid
            item
            key={index}
            xs={12}
            lg={6}
            xl={4}
            display="flex"
            justifyContent="center"
          >
            <SubscriptionCard
              {...item}
              onClick={onSubscription}
              status={
                item.id === userSubscription?.planId
                  ? userSubscription?.status
                  : undefined
              }
              disabled={
                item.id === null ||
                (userSubscription?.planId !== null && item.id !== userSubscription?.planId)
              }
              manageSubscription={
                (userSubscription?.planId !== null && item.id === userSubscription?.planId)
              }
            />
          </Grid>
        ))}
        {
          userSubscription && promoCodePlans[userSubscription.type] && (
            <Grid
              item
              xs={12}
              lg={6}
              xl={4}
              display="flex"
              justifyContent="center"
            >
              <SubscriptionCard
                {...promoCodePlans[userSubscription.type]}
                onClick={onSubscription}
                status={
                  userSubscription?.status
                }
                manageSubscription
              />
            </Grid>
          )
        }
      </Grid>
    </Box>
  );
}

export default SubscriptionPage;
